import { useEffect, useRef, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useElementSize } from "usehooks-ts";
import { useWidgetContext } from "../../../../helpers/hooks/useWidgetContext";
import { fetchData } from "../../../../helpers/offthreadFetcher";
import { configureUrlWithParams } from "../../../../helpers/utils";
import { RootState } from "../../../../state/store";
import LoadingAnimation from "../../../../components/loadingAnimation";
import SearchInput from "./searchInput";
import TableVisual from "./tableVisual";
import type { TableSearchListState } from "./types";
import { initialConfig } from "./utils";
import WidgetEmptyData from "../../../widgetEmptyData";
import WidgetLoading from "../../../widgetLoading";

const TableSearchListWidget = () => {
  const [widgetConfig, setWidgetConfig] = useState<TableSearchListState>({
    title: "",
    config: initialConfig,
    data: [],
    isLoading: false,
    isSearchLoading: false,
    error: null,
  });
  const widget = useWidgetContext();
  const routeMatch = useParams();
  const { filters, company } = useSelector((state: RootState) => state);
  const defaultDataRef = useRef<any[]>([]);

  const [tableWrapperRef, { width: tableWidth, height: tableHeight }] =
    useElementSize();

  useEffect(() => {
    if (widget.widgetLoading) {
      setWidgetConfig((prev) => ({
        ...prev,
        isLoading: true,
      }));
    } else {
      setWidgetConfig((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
    if (widget.widgetData) {
      if (widget.widgetData.title) {
        setWidgetConfig((prev) => ({
          ...prev,
          title: widget.widgetData.title,
        }));
      }
      if (widget.widgetData.config) {
        setWidgetConfig((prev) => ({
          ...prev,
          config: widget.widgetData.config,
        }));
      }
      if (widget.widgetData.data && widget.widgetData.data.length > 0) {
        defaultDataRef.current = widget.widgetData.data;
        setWidgetConfig((prev) => ({
          ...prev,
          data: widget.widgetData.data,
        }));
      }
    }
  }, [widget]);

  const onSearch = (query: string) => {
    let searchFullUrl = configureUrlWithParams(
      `/api/v1/dar/${routeMatch.dashboard}/${routeMatch.widget}/${widget.widgetUrl}/search`,
      {
        ...filters,
        shouldUseEpocForTimeRange: true,
        timezoneOffset: (company as any)?.company_details
          ?.time_zone_offset as string,
      }
    );
    // if the search endpoint already has query param, append the search query to it
    // else add the search query to the endpoint
    if (query && query.length > 2) {
      if (searchFullUrl.includes("?")) {
        searchFullUrl = `${searchFullUrl}&query=${query}`;
      } else {
        searchFullUrl = `${searchFullUrl}?query=${query}`;
      }
      setWidgetConfig((prev) => ({
        ...prev,
        isSearchLoading: true,
      }));
      fetchData(searchFullUrl)
        .then((res: any) => {
          setWidgetConfig((prev) => ({
            ...prev,
            data: res?.widget?.data,
            isSearchLoading: false,
          }));
        })
        .catch((err) => {
          console.error("Error fetching search data", err);
          setWidgetConfig((prev) => ({
            ...prev,
            data: [],
            isSearchLoading: false,
          }));
        });
    }
  };
  const onSearchReset = () => {
    setWidgetConfig((prev) => ({
      ...prev,
      data: defaultDataRef.current,
      isSearchLoading: false,
    }));
  };

  return (
    <Wrapper ref={tableWrapperRef}>
      {widgetConfig.isLoading ? (
        <WidgetLoading />
      ) : (
        <>
          <div className="widget-header">
            <div className="widget-title">{widgetConfig.title}</div>
            <div className="widget-controls">
              {widgetConfig.config.search.enabled && (
                <SearchInput
                  onSearch={onSearch}
                  onReset={onSearchReset}
                  placeholder={widgetConfig.config.search.placeholder}
                />
              )}
            </div>
          </div>
          <div className="widget-content">
            {widgetConfig.data.length > 0 &&
            defaultDataRef.current.length > 0 &&
            !widgetConfig.isLoading ? (
              <>
                {widgetConfig.isSearchLoading ? (
                  <div className="loading-container">
                    <LoadingAnimation />
                  </div>
                ) : (
                  <TableVisual
                    data={widgetConfig.data as any}
                    config={widgetConfig.config.table}
                    moreDetailsConfig={widgetConfig.config.more_details_dialog}
                    width={tableWidth - 50}
                    height={tableHeight - 90}
                  />
                )}
              </>
            ) : (
              <WidgetEmptyData
                width={tableWidth - 50}
                height={tableHeight - 70}
              />
            )}
          </div>
        </>
      )}
    </Wrapper>
  );
};

export default TableSearchListWidget;

const Wrapper = styled.div`
  border-radius: 20px;
  width: 100%;
  height: 100%;
  padding: 25px;
  background-color: ${(props) => props.theme.widget.background};

  .widget-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .widget-title {
      font-size: 23px;
      font-weight: 500;
      color: ${(props) => props.theme.text};
      text-decoration: underline;
    }
    .widget-controls {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }

  .widget-content {
    height: calc(100% - 60px);
    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      width: 100%;
    }
  }
`;
